import type { FeatureFlags } from '@/lib/api'

/**
 * Route definition with optional feature flag gating.
 * Single source of truth for admin/worker route paths and their FF dependencies.
 */
export interface RouteEntry {
  path: string
  /** Feature flag key that must be enabled for this route. */
  featureFlag?: keyof FeatureFlags
}

// ─── Admin routes ────────────────────────────────────────────
export const adminRoutes = {
  dashboard: { path: '/admin' },
  nomenclature: { path: '/admin/nomenclature' },

  tasks: { path: '/admin/tasks', featureFlag: 'enable_tasks' },

  shipments: { path: '/admin/shipments', featureFlag: 'enable_shipments' },
  shipmentDetail: (id: string) => `/admin/shipments/${id}`,

  settings: { path: '/admin/settings' },
  labelTemplates: { path: '/admin/settings/label-templates' },
  labelEditor: { path: '/admin/label-editor' },
  labelEditorTemplate: (name: string) => `/admin/label-editor/${encodeURIComponent(name)}`,

  kaTemplates: { path: '/admin/ka-templates' },
  kaGeneration: { path: '/admin/ka-generation', featureFlag: 'enable_ka_generation' },
  kaGenerationDoc: (id: string) => `/admin/ka-generation/${id}`,

  contractors: { path: '/admin/contractors' },
  infrastructure: { path: '/admin/infrastructure' },
  advanced: { path: '/admin/advanced' },
  metadata: { path: '/admin/metadata' },
  sidebarSettings: { path: '/admin/sidebar-settings' },
} as const satisfies Record<string, RouteEntry | ((...args: string[]) => string)>

// ─── Worker routes ───────────────────────────────────────────
export const workerRoutes = {
  dashboard: { path: '/worker' },
  tasks: { path: '/worker/tasks', featureFlag: 'enable_tasks' },
  taskDetail: (taskId: string) => `/worker/tasks/${taskId}`,
  shipments: { path: '/worker/shipments', featureFlag: 'enable_shipments' },
  shipmentDetail: (id: string) => `/worker/shipments/${id}`,
} as const satisfies Record<string, RouteEntry | ((...args: string[]) => string)>

/**
 * Feature-flag-gated route entries.
 * Used by FeatureGuard to determine which routes need flag checks.
 */
export const featureFlagRouteMap: Record<string, keyof FeatureFlags> = {
  // Admin
  '/admin/tasks': 'enable_tasks',
  '/admin/shipments': 'enable_shipments',
  '/admin/ka-generation': 'enable_ka_generation',
  // Worker
  '/worker/shipments': 'enable_shipments',
  '/worker/tasks': 'enable_tasks',
}
